import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { permanentRedirect } from "@/lib/legacyRedirects";
import { seoHeadFromPage } from "@/config/seoHead";
import { buildCompanyTitle } from "@/config/seoTitles";
import { findTitleTemplate, templateTitle } from "@/config/seoTitleTemplates";
import { cleanArticleTitle } from "@/lib/articleContent";
import { scrapedPageQueryOptions } from "@/hooks/useScrapedPage";
import { companyOffersQueryOptions } from "@/hooks/useCompanyOffers";
import { companyFeedAgreementsQueryOptions } from "@/hooks/useCompanyFeedAgreements";
import { lookupSlug, notFoundUnlessRedirected } from "@/lib/slugGuards";

const ElhandelDetalj = lazy(() => import("@/pages/content/ElhandelDetalj"));

export const Route = createFileRoute("/elhandel/$slug")({
  beforeLoad: ({ params }) => {
    // /elhandel/billiga är ersatt av /elbolag/billigaste.
    if (params.slug === "billiga") {
      throw permanentRedirect("/elbolag/billigaste");
    }
  },
  // Ladda innehåll + erbjudanden i SSR så sidan inte hoppar till efter hydrering.
  loader: async ({ params, context }) => {
    const path = `/elhandel/${params.slug}`;
    const page = await lookupSlug(() => context.queryClient.ensureQueryData(scrapedPageQueryOptions(path)));
    // null = bolaget finns inte (404); undefined = uppslagningen misslyckades, rendera ändå.
    if (page === null) await notFoundUnlessRedirected(path);
    const name = page?.title?.split(/[–—|:]/)[0]?.trim() ?? null;
    const [offers, feed] = await Promise.all([
      context.queryClient.ensureQueryData(companyOffersQueryOptions(params.slug, name)),
      context.queryClient.ensureQueryData(
        companyFeedAgreementsQueryOptions(params.slug, name, "SE3", 5000),
      ),
    ]);
    return { page, offers, feed };
  },
  // Titelmall: "Bolag » Priser, Elavtal & Recension | Elmarknad.se" (även i SSR-svaret).
  head: ({ params, loaderData }) => {
    const path = `/elhandel/${params.slug}`;
    const raw = cleanArticleTitle(loaderData?.page?.title) || loaderData?.page?.title || "";
    const company = (raw.split(/[–—|:]/)[0] ?? raw)
      .replace(/\s+(elavtal|elpriser|priser|omdömen?|recension|elhandel|elnät).*$/i, "")
      .trim();
    // Specifik titelmall (t.ex. /elhandel/billiga) vinner framför bolagsmallen.
    const template = findTitleTemplate(path);
    const templateTitleValue = template?.base
      ? templateTitle(path, company || "")
      : null;
    const titleOverride = templateTitleValue
      ? { title: templateTitleValue }
      : company
        ? { title: buildCompanyTitle(company) }
        : undefined;
    return seoHeadFromPage(path, loaderData?.page, {
      ogType: "article",
      ...titleOverride,
    });
  },
  component: ElhandelRoute,
});

function ElhandelRoute() {
  const { page, offers, feed } = Route.useLoaderData();
  return <ElhandelDetalj initialPage={page} initialOffers={offers} initialFeed={feed} />;
}
